import { faClose } from '@fortawesome/free-solid-svg-icons';
import { Loader } from '.';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Party } from 'service';
import ArthaGateway from 'service/ArthaGateway';

type NewCustomerProps = {
    name?: string;
    initialParty?: Party | null;
    onCustomerAdded?: (name?: string) => void;
    onCustomerSaved?: (party: Party) => void;
    onClose?: () => void;
}

const buildDefaultCustomer = (name: string): Party => ({
    id: 0,
    name: name,
    contactPerson: '',
    searchName: '',
    mobileNumber: '',
    groupName: '',
    type: 'Customer',
    groupId: 1,
    category: 'Individual',
    accountType: 'Global',
    email: '',
    code: '',
    registrationType: 'Registered',
    gstin: '',
    pan: '',
    billingAddress1: '',
    billingAddress2: '',
    billingCity: '',
    billingStateCode: 29,
    billingPinCode: '',
    shippingAddress1: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingStateCode: 29,
    shippingPinCode: '',
    openingBalance: 0,
    openingBalanceAsOn: moment(new Date()).format("YYYY-MM-DD"),
    createdBy: 1,
    isExpanded: false,
    accountId: 0
});

export const NewCustomer = ({ name = "", initialParty = null, onCustomerAdded, onCustomerSaved, onClose }: NewCustomerProps) => {
    const [party, setParty] = useState<Party>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isEditMode = (party?.id || 0) > 0;

    useEffect(() => {
        const baseParty = buildDefaultCustomer(name);
        setParty(initialParty ? { ...baseParty, ...initialParty } : baseParty);
    }, [name, initialParty]);

    const handlePartyUpdate = (name: string, value: any) => {
        if (party) {
            setParty({ ...party, [name]: value });
        }
    }

    const createCustomer = () => {
        if (!party) {
            return;
        }

        setIsLoading(true);
        ArthaGateway.createCustomer(party).then((response) => {
            setIsLoading(false);
            if (response > 0) {
                const savedParty = { ...party, id: party.id > 0 ? party.id : response };
                if (onCustomerSaved) {
                    onCustomerSaved(savedParty);
                }
                if (onCustomerAdded) {
                    onCustomerAdded(party?.name);
                }
            }
            else {
                toast.error(isEditMode ? "Error updating customer" : "Error creating customer");
            }
        }).catch((error) => {
            setIsLoading(false);
            console.log(error);
            toast.error(isEditMode ? "Error updating customer" : "Error creating customer");
        });
    }

    return (
        <form>
            <div className='page page-new-customer'>
                <div className='container'>
                    <div className='row justify-content-center'>
                        <div className="col-12 col-lg-8">
                            <div className="w-100">
                                <h1 className="float-start">{isEditMode ? 'Edit Customer' : 'New Customer'}</h1>
                                <div className="btn-close btn-sm float-end justify-content-center" onClick={onClose}>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row justify-content-center'>
                        <div className="col-12 col-lg-8">
                            <div className="row">
                                <div className="form-outline mb-2">
                                    <label className="form-label">Name</label>
                                    <input type="text" name="name" className="form-control" value={party?.name} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} autoFocus />
                                </div>
                            </div>

                            <div className="row"> {/* Nested row for side-by-side fields */}

                                <div className="col-12 col-md-6 mb-2"> {/* Added col-md-6 for half width on medium and larger screens */}
                                    <div className="form-outline">
                                        <label className="form-label">Mobile</label>
                                        <input type="number" name="mobileNumber" className="form-control" value={party?.mobileNumber} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} maxLength={10} />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 mb-2"> {/* Added col-md-6 for half width on medium and larger screens */}
                                    <div className="form-outline">
                                        <label className="form-label">Email</label>
                                        <input type="text" name="email" className="form-control" value={party?.email} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} />
                                    </div>
                                </div>
                                {/*<div className="col-12 col-md-6 mb-2">  Added col-md-6 for half width on medium and larger screens 
                                    <div className="form-outline">
                                        <label className="form-label">GSTIN</label>
                                        <input type="text" name="gstin" className="form-control" value={party?.gstin} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} />
                                    </div>
                                </div> */}
                            </div>

                            <div className="form-outline mb-2">
                                <label className="form-label">Address 1</label>
                                <input type="text" name="billingAddress1" className="form-control" value={party?.billingAddress1} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} />
                            </div>

                            <div className="form-outline mb-2">
                                <label className="form-label">Address 2</label>
                                <input type="text" name="billingAddress2" className="form-control" value={party?.billingAddress2} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} />
                            </div>

                            <div className="row"> {/* Nested row for side-by-side fields */}
                                <div className="col-12 col-md-6 mb-2"> {/* Added col-md-6 for half width on medium and larger screens */}
                                    <div className="form-outline">
                                        <label className="form-label">City</label>
                                        <input type="text" name="billingCity" className="form-control" value={party?.billingCity} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6 mb-2"> {/* Added col-md-6 for half width on medium and larger screens */}
                                    <div className="form-outline">
                                        <label className="form-label">Pincode</label>
                                        <input type="number" name="billingPinCode" className="form-control" value={party?.billingPinCode} onChange={(e) => handlePartyUpdate(e.target.name, e.target.value)} /> {/* Corrected name attribute */}
                                    </div>
                                </div>
                            </div>

                            {party && party.name.length > 3 &&
                                <div className="row bg-light" style={{ borderTop: '1px solid #ccc' }}>
                                    <div className="btn btn-primary bg-dark w-100 text-center">
                                        {isLoading &&
                                            <Loader />
                                        }
                                        {!isLoading &&
                                            <div className='w-100' onClick={() => createCustomer()}>
                                                {isEditMode ? 'Save' : 'Create'}
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};